<template>
  <v-app class="hc-compact-page appointment-calendar-page">
    <PageHeader
      :title="`Kalender Pendaftaran atau Perjanjian ${clinic_name}`"
      subtitle="Visualisasi jadwal pendaftaran dan perjanjian dokter"
      icon="far fa-calendar-alt"
    >
      <template #actions>
        <div class="calendar-page-actions">
          <b-button variant="outline-primary" @click="$router.push('/appointment/list/0')">
            <i class="fas fa-list"></i><span>Lihat Daftar</span>
          </b-button>
          <b-button v-if="manipulateBtn" variant="primary" @click="$router.push('/appointment/add')">
            <i class="fas fa-plus"></i><span>Tambah Perjanjian</span>
          </b-button>
        </div>
      </template>
    </PageHeader>

    <section class="calendar-overview" aria-label="Ringkasan kalender">
      <div class="calendar-overview__intro">
        <div class="calendar-overview__icon"><i class="far fa-calendar-check"></i></div>
        <div>
          <span>Ringkasan jadwal</span>
          <strong>{{ calendarPeriodLabel }}</strong>
        </div>
      </div>
      <div class="calendar-overview__stats">
        <div class="calendar-stat">
          <span>Total agenda</span>
          <strong>{{ events.length }}</strong>
        </div>
        <div class="calendar-stat calendar-stat--today">
          <span>Hari ini</span>
          <strong>{{ todayEventCount }}</strong>
        </div>
        <div class="calendar-stat calendar-stat--doctor">
          <span>Dokter tersedia</span>
          <strong>{{ doctors.length || '-' }}</strong>
        </div>
      </div>
    </section>

    <Card
      card-class="appointment-calendar-shell"
      head-size="sm"
    >
      <template v-slot:body>
        <div class="calendar-workspace">
          <aside class="calendar-sidebar" aria-label="Navigasi dan filter kalender">
            <section class="calendar-side-card calendar-filter-card hc-no-form-icons">
              <div class="calendar-section-heading calendar-section-heading--compact">
                <div>
                  <span class="calendar-eyebrow">Tampilan jadwal</span>
                  <h3>Filter kalender</h3>
                </div>
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="calendar-reset-button"
                  @click="resetFilters"
                >
                  Reset
                </button>
              </div>

              <label class="calendar-filter-field" for="input-status">
                <span class="calendar-filter-label">
                  <i class="far fa-check-circle" aria-hidden="true"></i>
                  Status perjanjian
                </span>
                <treeselect
                  id="input-status"
                  v-model="filter.appointment_status_id"
                  :multiple="false"
                  :options="appointmentStatuses"
                  placeholder="Semua status"
                  @select="filterStatusOnSelect"
                />
              </label>

              <label
                v-if="currentUser.user_type_id != 1"
                class="calendar-filter-field"
                for="input-doctor"
              >
                <span class="calendar-filter-label">
                  <i class="fas fa-user-md" aria-hidden="true"></i>
                  Dokter
                </span>
                <treeselect
                  id="input-doctor"
                  v-model="filter.doctor_id"
                  :multiple="false"
                  :options="doctors"
                  placeholder="Semua dokter"
                  @select="filterDoctorOnSelect"
                />
              </label>
            </section>

            <section class="calendar-side-card calendar-legend-card">
              <div class="calendar-section-heading calendar-section-heading--compact">
                <div>
                  <span class="calendar-eyebrow">Keterangan</span>
                  <h3>Status perjanjian</h3>
                </div>
              </div>
              <div class="calendar-status-list">
                <button
                  v-for="color in statusColor"
                  :key="color.id"
                  type="button"
                  class="calendar-status-chip"
                  :class="[
                    `calendar-status-chip--${getStatusTone(color.color_class)}`,
                    { 'calendar-status-chip--active': filter.appointment_status_id === color.id }
                  ]"
                  @click="selectStatus(color)"
                >
                  <i :class="getStatusIconClass(color.color_class)" aria-hidden="true"></i>
                  <span>{{ color.name }}</span>
                </button>
              </div>
            </section>
          </aside>

          <main class="calendar-main">
            <header class="calendar-main__toolbar">
              <div class="calendar-main__title">
                <span class="calendar-eyebrow">Jadwal klinik</span>
                <h2 v-if="$refs.calendar">{{ $refs.calendar.title }}</h2>
                <h2 v-else>Kalender perjanjian</h2>
              </div>

              <div class="calendar-toolbar-actions">
                <button type="button" class="calendar-today-button" @click="setToday">
                  <i class="far fa-calendar-check" aria-hidden="true"></i>
                  Hari ini
                </button>
                <div class="calendar-nav-group" aria-label="Navigasi periode kalender">
                  <button type="button" aria-label="Periode sebelumnya" @click="prev">
                    <i class="fas fa-chevron-left" aria-hidden="true"></i>
                  </button>
                  <button type="button" aria-label="Periode berikutnya" @click="next">
                    <i class="fas fa-chevron-right" aria-hidden="true"></i>
                  </button>
                </div>

                <v-menu bottom right offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="calendar-view-button"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ typeToLabel[type] }}
                      <i class="fas fa-chevron-down" aria-hidden="true"></i>
                    </button>
                  </template>
                  <v-list class="calendar-view-menu">
                    <v-list-item @click="type = 'day'">
                      <v-list-item-title>Per Hari</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="type = 'week'">
                      <v-list-item-title>Per Minggu</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="type = 'month'">
                      <v-list-item-title>Per Bulan</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="type = '4day'">
                      <v-list-item-title>Per 4 Hari</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </header>

            <div class="calendar-main__body" :class="{ 'calendar-main__body--loading': loading }">
              <div v-if="loadError" class="calendar-feedback calendar-feedback--error" role="alert">
                <div><i class="fas fa-exclamation-circle"></i></div>
                <strong>Kalender belum dapat dimuat</strong>
                <span>{{ loadError }}</span>
                <button type="button" @click="getEvents">Coba lagi</button>
              </div>
              <div v-else-if="loading" class="calendar-feedback calendar-feedback--loading" aria-live="polite">
                <b-spinner small variant="primary" />
                <span>Memuat jadwal perjanjian...</span>
              </div>
              <div v-else-if="events.length === 0" class="calendar-empty-hint">
                <i class="far fa-calendar-times"></i>
                <span>Belum ada agenda pada filter ini</span>
              </div>
              <v-calendar
                ref="calendar"
                v-model="value"
                locale="id"
                :weekdays="weekday"
                :type="type"
                :events="events"
                :event-overlap-threshold="30"
                :event-color="getEventColor"
                @click:event="showEvent"
                @click:more="viewDay"
                @click:date="viewDay"
                @change="getEvents"
              />
            </div>
          </main>
        </div>

        <v-menu
          v-model="selectedOpen"
          :close-on-content-click="false"
          absolute
          :position-x="popoverX"
          :position-y="popoverY"
          content-class="calendar-event-menu"
          transition="scale-transition"
        >
          <v-card class="calendar-event-popover" flat>
            <div
              class="calendar-event-popover__header"
              :class="`calendar-event-popover__header--${selectedEvent.tone || 'neutral'}`"
            >
              <div class="calendar-event-popover__heading">
                <span>Detail agenda</span>
                <h3>{{ selectedEvent.name || 'Perjanjian' }}</h3>
              </div>
              <button
                type="button"
                class="calendar-event-popover__close"
                aria-label="Tutup detail agenda"
                @click="selectedOpen = false"
              >
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <v-card-text class="calendar-event-popover__content">
              <div class="calendar-event-detail-row calendar-event-detail-row--schedule">
                <i class="far fa-calendar-alt" aria-hidden="true"></i>
                <div>
                  <span>Jadwal</span>
                  <strong>{{ selectedEvent.dayName || '-' }}</strong>
                  <small>{{ selectedEvent.startLabel || '-' }} s/d {{ selectedEvent.endLabel || '-' }}</small>
                </div>
              </div>
              <div class="calendar-event-detail-row">
                <i class="fas fa-user-md" aria-hidden="true"></i>
                <div><span>Dokter</span><strong>{{ selectedEvent.doctor_name || '-' }}</strong></div>
              </div>
              <div class="calendar-event-detail-row">
                <i :class="getStatusIconClass(selectedEvent.color)" aria-hidden="true"></i>
                <div><span>Status</span><strong>{{ selectedEvent.status_name || '-' }}</strong></div>
              </div>
              <div class="calendar-event-detail-row">
                <i class="fas fa-hand-holding-medical" aria-hidden="true"></i>
                <div><span>Tindakan</span><strong>{{ selectedEvent.action_type_name || '-' }}</strong></div>
              </div>
              <div class="calendar-event-note">
                <span><i class="far fa-sticky-note" aria-hidden="true"></i> Catatan</span>
                <p>{{ selectedEvent.notes || 'Tidak ada catatan.' }}</p>
              </div>
              <div v-if="selectedEvent.id" class="calendar-event-popover__footer">
                <button
                  type="button"
                  @click="goToAppointmentDetail(selectedEvent.id)"
                >
                  Lihat detail perjanjian <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </template>
    </Card>
  </v-app>
</template>

<script>

import Card from "@/view/content/Card.vue"
import module from "@/core/modules/CrudModule.js"
import Treeselect from "@riophae/vue-treeselect"
import "@riophae/vue-treeselect/dist/vue-treeselect.css"
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module"
import { getUser } from "@/core/services/jwt.service.js"
import DoctorModule from "@/core/modules/DoctorModule"
import AppointmentModule from "@/core/modules/AppointmentModule"
import PageHeader from "@/component/general/PageHeader.vue"

export default {
  components: {
    Card,
    Treeselect,
    PageHeader,
  },

  data: () => ({
    filter: {
      doctor_id: null,
      appointment_status_id: null,
    },
    statusColor: [],
    doctors: [],
    appointmentStatuses: [],
    type: "month",
    typeToLabel: {
      month: "Per Bulan",
      week: "Per Minggu",
      day: "Per Hari",
      "4day": "Per 4 Hari",
    },
    weekday: [0, 1, 2, 3, 4, 5, 6],
    value: "",
    events: [],
    loading: false,
    loadError: "",
    manipulateBtn: false,
    colors: ["bg-filed", "bt-conf-wait", "bt-conf", "bt-handled", "bg-danger"],
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    popoverX: 16,
    popoverY: 16,
    currentUser: getUser() || {},
    clinic_name: window.configuration == null ? "Medhop" : window.configuration.clinic_name,
  }),

  methods: {

    selectCalendarDate(date) {
      this.value = date;
    },

    async resetFilters() {
      this.filter.appointment_status_id = null;
      this.filter.doctor_id = this.currentUser.user_type_id == 1
        ? this.currentUser.user_id
        : null;
      await this.getEvents();
    },

    async selectStatus(status) {
      this.filter.appointment_status_id =
        this.filter.appointment_status_id === status.id ? null : status.id;
      await this.getEvents();
    },

    async getDoctors() {
      const data = await DoctorModule.getTreeSelect()
      this.doctors = Array.isArray(data) ? data : []
    },

    async getAppointmentStatuses() {
      const data = await AppointmentModule.getSimpleAppointmentStatuses()
      this.statusColor = Array.isArray(data) ? data : []
      this.appointmentStatuses = []
      this.statusColor.forEach(value => {
        this.appointmentStatuses.push({
          id: value.id,
          label: value.name
        })
      })
    },

    async getAppointment() {
      return await module.list(
        `appointments/get-calendar?doctor_id=${this.filter.doctor_id || ""}&appointment_status_id=${this.filter.appointment_status_id || ""}`
      );
    },

    async filterDoctorOnSelect(evt) {
      await this.filter.doctor_id;
      this.getEvents();
    },

    async filterStatusOnSelect(evt) {
      await this.filter.appointment_status_id;
      this.getEvents();
    },

    async getEvents() {
      this.loading = true;
      this.loadError = "";
      try {
        const response = await this.getAppointment();
        const appointments = Array.isArray(response) ? response : [];
        this.events = appointments.map((appointment) => ({
          id: appointment.id,
          name: appointment.name || appointment.patient_name || "Perjanjian pasien",
          start: new Date(appointment.start_time),
          end: new Date(appointment.end_time),
          doctor_name: appointment.doctor_name,
          notes: appointment.notes,
          status_name: appointment.appointment_status_name,
          action_type_name: appointment.action_type_name,
          color: this.getAppointmentEventColor(appointment),
          tone: this.getAppointmentEventTone(appointment),
          timed: true,
        })).filter((event) => !Number.isNaN(event.start.getTime()) && !Number.isNaN(event.end.getTime()));
      } catch (error) {
        this.events = [];
        this.loadError = "Periksa koneksi data lalu muat kembali halaman ini.";
      } finally {
        this.loading = false;
      }
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        let dayName = new Date(event.start).toLocaleDateString("id-ID", {
          weekday: "long",
        });
        let start = new Date(event.start).toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        });
        let end = new Date(event.end).toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        });
        this.selectedEvent.dayName = dayName;
        this.selectedEvent.startLabel = start;
        this.selectedEvent.endLabel = end;

        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const popoverWidth = Math.min(330, viewportWidth - 32);
        const popoverHeight = 410;
        const maxX = Math.max(16, viewportWidth - popoverWidth - 16);
        const maxY = Math.max(16, viewportHeight - popoverHeight - 16);
        this.popoverX = Math.min(maxX, Math.max(16, nativeEvent.clientX - popoverWidth - 18));
        this.popoverY = Math.min(maxY, Math.max(16, nativeEvent.clientY - 90));
        setTimeout(() => {
          this.selectedOpen = true;
        }, 30);
      };
      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 30);
      } else {
        open();
      }
      nativeEvent.stopPropagation();
    },

    viewDay({ date }) {
      this.value = date;
      this.type = "day";
    },

    setToday() {
      this.value = window.medhopToday();
    },

    prev() {
      this.$refs.calendar.prev();
    },

    next() {
      this.$refs.calendar.next();
    },

    getEventColor(event) {
      return event.color;
    },

    getStatusBadgeClass(colorClass) {
      if (!colorClass) return 'badge-secondary';
      if (colorClass.includes('bg-filed') || colorClass.includes('primary')) return 'badge-primary';
      if (colorClass.includes('bt-conf-wait') || colorClass.includes('warning')) return 'badge-warning';
      if (colorClass.includes('bt-conf') || colorClass.includes('success')) return 'badge-success';
      if (colorClass.includes('bt-handled') || colorClass.includes('info')) return 'badge-info';
      if (colorClass.includes('bg-danger') || colorClass.includes('danger')) return 'badge-danger';
      return 'badge-secondary';
    },

    getStatusIconClass(colorClass) {
      const status = String(colorClass || "").toLowerCase();
      if (!status) return 'far fa-question-circle';
      if (status.includes('filed') || status.includes('primary')) return 'far fa-paper-plane';
      if (status.includes('conf-wait') || status.includes('warning')) return 'fas fa-hourglass-half';
      if (status.includes('conf') || status.includes('success')) return 'far fa-check-circle';
      if (status.includes('handled') || status.includes('info')) return 'fas fa-check-double';
      if (status.includes('danger') || status.includes('cancel')) return 'far fa-times-circle';
      return 'far fa-question-circle';
    },

    getStatusTone(colorClass) {
      const status = String(colorClass || "").toLowerCase();
      if (!status) return "neutral";
      if (status.includes("filed") || status.includes("primary")) return "primary";
      if (status.includes("conf-wait") || status.includes("warning")) return "warning";
      if (status.includes("conf") || status.includes("success")) return "success";
      if (status.includes("handled") || status.includes("info")) return "info";
      if (status.includes("danger") || status.includes("cancel")) return "danger";
      return "neutral";
    },

    getAppointmentEventTone(appointment) {
      const status = `${appointment.appointment_status_name || ""} ${appointment.appointment_status_color_class || ""} ${appointment.color_class || ""}`.toLowerCase();
      if (status.includes("batal") || status.includes("danger") || status.includes("cancel")) return "danger";
      if (status.includes("menunggu") || status.includes("conf-wait") || status.includes("warning")) return "warning";
      if (status.includes("selesai") || status.includes("pulang")) return "success";
      if (status.includes("terkonfirmasi") || status.includes("conf") || status.includes("success")) return "success";
      if (status.includes("ditangani") || status.includes("handled") || status.includes("info")) return "info";
      return "primary";
    },

    getAppointmentEventColor(appointment) {
      const colors = {
        primary: "#246AA3",
        warning: "#B65D12",
        success: "#168C72",
        info: "#6452B8",
        danger: "#D93F4C",
      };
      return colors[this.getAppointmentEventTone(appointment)] || colors.primary;
    },

    goToAppointmentDetail(id) {
      this.selectedOpen = false;
      this.$router.push(`/appointment/detail/${id}`);
    },

    setActiveMenu() {
      try {
        const accessRights = JSON.parse(window.localStorage.getItem("access_right_display") || "[]");
        this.manipulateBtn = accessRights.includes("5002");
      } catch (error) {
        this.manipulateBtn = false;
      }
    },

    random(a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a;
    },
  },

  computed: {
    todayEventCount() {
      const today = new Date().toDateString();
      return this.events.filter((event) => event.start instanceof Date && event.start.toDateString() === today).length;
    },
    calendarPeriodLabel() {
      const date = this.value ? new Date(`${this.value}T00:00:00`) : new Date();
      if (Number.isNaN(date.getTime())) return "Kalender perjanjian";
      return new Intl.DateTimeFormat("id-ID", { month: "long", year: "numeric" }).format(date);
    },
    hasActiveFilters() {
      const hasStatus = this.filter.appointment_status_id !== null &&
        typeof this.filter.appointment_status_id !== "undefined";
      const hasDoctor = this.currentUser && this.currentUser.user_type_id != 1 &&
        this.filter.doctor_id !== null &&
        typeof this.filter.doctor_id !== "undefined";
      return hasStatus || hasDoctor;
    },
  },

  watch: {
    "filter.doctor_id": function (newVal, oldVal) {
      if (typeof newVal === "undefined") {
        this.filter.doctor_id = null;
        this.filterDoctorOnSelect();
      }
    },
    "filter.appointment_status_id": function (newVal, oldVal) {
      if (typeof newVal === "undefined") {
        this.filter.appointment_status_id = null;
        this.filterStatusOnSelect();
      }
    },
  },

  mounted() {
    this.setActiveMenu()
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Perjanjian", route: "" },
      { title: "Kalender" },
    ])
    this.$nextTick(() => {
      if (this.$refs.calendar) {
        this.$refs.calendar.checkChange()
        this.$refs.calendar.scrollToTime("08:00")
      }
    })
    if (this.currentUser.user_type_id != 1)
      this.getDoctors()
    else
      this.filter.doctor_id = this.currentUser.user_id
    this.getAppointmentStatuses()
  }

}
</script>

<style lang="scss">
@import "~vuetify/dist/vuetify.css";

.theme--light.v-application {
  background: transparent !important;
}

.v-application code {
  box-shadow: none !important;
}

.appointment-calendar-page .v-application--wrap {
  min-height: 0 !important;
}

.appointment-calendar-page .appointment-calendar-shell.card {
  overflow: hidden;
  border: 1px solid #E1E9F2 !important;
  border-radius: 18px !important;
  background: #FFFFFF !important;
  box-shadow: 0 10px 28px rgba(20, 50, 99, 0.06) !important;
  transform: none !important;
}

.appointment-calendar-page .appointment-calendar-shell > .card-header {
  min-height: 52px;
  padding: 0 2px 12px;
  border: 0;
  background: transparent;
}

.appointment-calendar-page .appointment-calendar-shell > .card-header .card-label {
  margin: 0;
  color: #1A2433;
  font-size: 20px;
  font-weight: 700;
}

.appointment-calendar-page .appointment-calendar-shell > .card-body {
  padding: 16px !important;
  background: #FFFFFF !important;
}

.calendar-workspace {
  display: grid;
  grid-template-columns: minmax(225px, 260px) minmax(0, 1fr);
  align-items: start;
  gap: 18px;
}

.calendar-sidebar {
  display: grid;
  gap: 14px;
}

.calendar-side-card,
.calendar-main {
  background: #FFFFFF;
  border: 1px solid #E7EDF4;
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(20, 50, 99, 0.06);
}

.calendar-side-card {
  padding: 16px;
}

.calendar-date-card {
  overflow: hidden;
  color: #FFFFFF;
  background: linear-gradient(145deg, #143263 0%, #14608F 58%, #1E958F 100%);
  border-color: transparent;
}

.calendar-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.calendar-section-heading--compact {
  margin-bottom: 13px;
}

.calendar-eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1E958F;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.11em;
  line-height: 1.2;
  text-transform: uppercase;
}

.calendar-section-heading h3,
.calendar-main__title h2 {
  margin: 0;
  color: #1A2433;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.calendar-section-heading h3 {
  font-size: 14px;
}

.calendar-date-card .calendar-eyebrow,
.calendar-date-card .calendar-section-heading h3 {
  color: #FFFFFF !important;
}

.calendar-date-card .calendar-eyebrow {
  opacity: 0.72;
}

.calendar-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #FFFFFF;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 11px;
}

.calendar-section-icon i {
  color: #FFFFFF !important;
}

.calendar-date-picker.v-picker {
  width: 100%;
  min-height: 0 !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  transform: none !important;
}

.calendar-date-picker .v-picker__body,
.calendar-date-picker .v-date-picker-header {
  background: transparent !important;
}

.calendar-date-picker .v-picker__body {
  min-height: 0 !important;
  height: auto !important;
}

.calendar-date-picker .v-date-picker-header {
  padding: 4px 0 8px !important;
}

.calendar-date-picker .v-date-picker-header__value button,
.calendar-date-picker .v-date-picker-header .v-btn,
.calendar-date-picker .v-date-picker-table th,
.calendar-date-picker .v-date-picker-table .v-btn {
  color: #FFFFFF !important;
}

.calendar-date-picker .v-date-picker-table th {
  opacity: 0.65;
  font-size: 10px;
  font-weight: 700;
}

.calendar-date-picker .v-date-picker-table {
  height: 190px !important;
  padding: 0 !important;
}

.calendar-date-picker .v-date-picker-table .v-btn {
  width: 28px !important;
  height: 28px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
}

.calendar-date-picker .v-date-picker-table .v-btn--active {
  color: #143263 !important;
  background: #FFFFFF !important;
  box-shadow: 0 5px 14px rgba(20, 50, 99, 0.22) !important;
}

.calendar-filter-field {
  display: block;
  margin: 0 0 12px;
}

.calendar-filter-field:last-child {
  margin-bottom: 0;
}

.calendar-filter-label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 7px;
  color: #1A2433;
  font-size: 12px;
  font-weight: 700;
}

.calendar-filter-label i {
  width: 16px;
  color: #1E958F;
  text-align: center;
}

.calendar-filter-card .vue-treeselect__control {
  height: 42px !important;
  min-height: 42px !important;
  padding: 0 4px;
  background: #F8FAFC !important;
  border: 1px solid #DCE5EF !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.calendar-filter-card .vue-treeselect__value-container {
  height: 40px !important;
  padding: 0 10px !important;
}

.calendar-filter-card .vue-treeselect__input-container {
  height: 40px !important;
  padding: 0 !important;
}

.calendar-filter-card input.vue-treeselect__input {
  width: 100% !important;
  height: 40px !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  color: #1A2433 !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  font: inherit !important;
  line-height: 40px !important;
}

.calendar-filter-card input.vue-treeselect__input:focus {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

.calendar-filter-card .vue-treeselect__control-arrow-container,
.calendar-filter-card .vue-treeselect__clear-container {
  width: 36px !important;
}

.calendar-filter-card .vue-treeselect--focused .vue-treeselect__control,
.calendar-filter-card .vue-treeselect--open .vue-treeselect__control {
  background: #FFFFFF !important;
  border-color: #1E958F !important;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.12) !important;
}

.calendar-filter-card .vue-treeselect__placeholder,
.calendar-filter-card .vue-treeselect__single-value {
  color: #6B7280 !important;
  font-size: 12px !important;
  font-weight: 600;
  line-height: 40px !important;
}

.calendar-filter-card .vue-treeselect__control-arrow {
  width: 8px;
  height: 8px;
  color: #6B7280;
  fill: #6B7280 !important;
}

.calendar-filter-card .vue-treeselect__menu {
  margin-top: 7px;
  border: 1px solid #E7EDF4 !important;
  border-radius: 14px !important;
  box-shadow: 0 18px 42px rgba(20, 50, 99, 0.16) !important;
}

.calendar-filter-card .vue-treeselect__option--highlight {
  background: rgba(30, 149, 143, 0.08) !important;
}

.calendar-reset-button {
  padding: 5px 9px;
  color: #14608F;
  background: rgba(20, 96, 143, 0.07);
  border: 0;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
}

.calendar-status-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.calendar-status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 18px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.calendar-status-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.calendar-status-chip i {
  display: inline-block !important;
  font-size: 14px;
}

.calendar-status-chip span {
  display: inline-block;
}

.calendar-status-chip--primary {
  color: #1871CF !important;
  background: #E7F1FC !important;
}
.calendar-status-chip--warning {
  color: #E56614 !important;
  background: #FFF0E4 !important;
}
.calendar-status-chip--success {
  color: #15965A !important;
  background: #E7F7EE !important;
}
.calendar-status-chip--info {
  color: #6F50C9 !important;
  background: #F0EBFB !important;
}
.calendar-status-chip--danger {
  color: #DF3D3D !important;
  background: #FDECEC !important;
}

.calendar-main {
  min-width: 0;
  overflow: hidden;
}

.calendar-main__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 74px;
  padding: 13px 17px;
  background: linear-gradient(110deg, rgba(20, 50, 99, 0.07), rgba(30, 149, 143, 0.06));
  border-bottom: 1px solid #E7EDF4;
}

.calendar-main__title h2 {
  font-size: 18px;
}

.calendar-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 7px;
}

.calendar-today-button,
.calendar-view-button,
.calendar-nav-group button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 35px;
  color: #143263;
  background: #FFFFFF;
  border: 1px solid #DCE5EF;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  transition: border-color 170ms ease, color 170ms ease, background 170ms ease, transform 170ms ease;
}

.calendar-today-button {
  gap: 7px;
  padding: 0 11px;
}

.calendar-today-button i {
  color: #1E958F;
}

.calendar-view-button {
  gap: 10px;
  min-width: 108px;
  padding: 0 11px;
}

.calendar-view-button i {
  font-size: 9px;
  color: #6B7280;
}

.calendar-nav-group {
  display: inline-flex;
  overflow: hidden;
  background: #FFFFFF;
  border: 1px solid #DCE5EF;
  border-radius: 10px;
}

.calendar-nav-group button {
  width: 34px;
  border: 0;
  border-radius: 0;
}

.calendar-nav-group button + button {
  border-left: 1px solid #E7EDF4;
}

.calendar-today-button:hover,
.calendar-view-button:hover,
.calendar-nav-group button:hover {
  color: #FFFFFF;
  background: #14608F;
  border-color: #14608F;
  transform: translateY(-1px);
}

.calendar-today-button:hover i,
.calendar-view-button:hover i {
  color: #FFFFFF;
}

.calendar-main__body {
  height: 560px;
  overflow: auto;
  background: #FFFFFF;
}

.calendar-main__body > .v-calendar {
  min-width: 680px;
  height: 100%;
}

.calendar-main .v-calendar-weekly {
  background: #FFFFFF !important;
  border: 0 !important;
}

.calendar-main .v-calendar-weekly__head {
  background: #F8FAFC !important;
  border-bottom: 1px solid #E7EDF4 !important;
}

.calendar-main .v-calendar-weekly__head-weekday {
  padding: 10px 0 !important;
  color: #6B7280 !important;
  border-right: 1px solid #E7EDF4 !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.calendar-main .v-calendar-weekly__day {
  background: #FFFFFF !important;
  border-right: 1px solid #E7EDF4 !important;
  border-bottom: 1px solid #E7EDF4 !important;
  transition: background 160ms ease;
}

.calendar-main .v-calendar-weekly__day:hover {
  background: #F7FAFC !important;
}

.calendar-main .v-calendar-weekly__day.v-outside {
  background: #FBFCFE !important;
}

.calendar-main .v-calendar-weekly__day-label {
  margin: 6px !important;
}

.calendar-main .v-calendar-weekly__day-label .v-btn {
  width: 28px !important;
  height: 28px !important;
  color: #536174 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
}

.calendar-main .v-calendar-weekly__day.v-outside .v-btn {
  color: #AAB4C2 !important;
}

.calendar-main .v-calendar-weekly__day.v-present .v-btn {
  color: #FFFFFF !important;
  background: linear-gradient(135deg, #143263, #1E958F) !important;
  box-shadow: 0 5px 12px rgba(30, 149, 143, 0.25) !important;
}

.calendar-main .v-calendar .v-event {
  min-height: 22px !important;
  margin: 2px 4px !important;
  padding: 3px 7px !important;
  border: 1px solid rgba(255, 255, 255, 0.44) !important;
  border-radius: 9px !important;
  box-shadow: 0 5px 12px rgba(20, 50, 99, 0.13) !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
}

.calendar-view-menu {
  min-width: 180px;
  padding: 7px !important;
  border: 1px solid #E7EDF4;
  border-radius: 14px !important;
  box-shadow: 0 18px 42px rgba(20, 50, 99, 0.16) !important;
}

.calendar-view-menu .v-list-item {
  min-height: 40px;
  border-radius: 9px;
}

.calendar-view-menu .v-list-item:hover {
  background: rgba(30, 149, 143, 0.08) !important;
}

.calendar-event-popover {
  width: min(330px, calc(100vw - 32px));
  overflow: hidden;
  border: 1px solid #E7EDF4 !important;
  border-radius: 18px !important;
  background: #FFFFFF !important;
  box-shadow: 0 22px 54px rgba(20, 50, 99, 0.23) !important;
}

.calendar-event-popover__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 17px 18px 15px 21px;
  border-bottom: 1px solid #E7EDF4;
  background: linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%);
}

.calendar-event-popover__header::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: #356FD0;
  content: "";
}

.calendar-event-popover__header--success::before { background: #1E958F; }
.calendar-event-popover__header--warning::before { background: #E3A326; }
.calendar-event-popover__header--danger::before { background: #D65353; }
.calendar-event-popover__header--info::before { background: #278AB0; }
.calendar-event-popover__header--neutral::before { background: #718096; }

.calendar-event-popover__heading {
  min-width: 0;
}

.calendar-event-popover__heading span {
  display: block;
  margin-bottom: 3px;
  color: #8090A2;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.calendar-event-popover__heading h3 {
  margin: 0;
  overflow: hidden;
  color: #1E293B !important;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.calendar-event-popover__close {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 9px;
  background: #EEF2F6;
  color: #6E7E90;
  transition: background 0.15s ease, color 0.15s ease;
}

.calendar-event-popover__close:hover {
  background: #E4EAF0;
  color: #263548;
}

.calendar-event-popover__content {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  padding: 17px 18px 18px !important;
  color: #536174 !important;
  font-size: 12px;
  line-height: 1.5;
}

.calendar-event-detail-row {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 9px;
}

.calendar-event-detail-row > i {
  width: 29px;
  height: 29px;
  flex: 0 0 29px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #EFF7F6;
  color: #1E958F;
  font-size: 11px;
}

.calendar-event-detail-row div {
  min-width: 0;
}

.calendar-event-detail-row span,
.calendar-event-detail-row strong,
.calendar-event-detail-row small {
  display: block;
}

.calendar-event-detail-row span {
  margin-bottom: 2px;
  color: #8A99AA;
  font-size: 10px;
  font-weight: 700;
}

.calendar-event-detail-row strong {
  color: #344256;
  font-size: 12px;
  font-weight: 750;
  overflow-wrap: anywhere;
}

.calendar-event-detail-row small {
  margin-top: 2px;
  color: #1E958F;
  font-size: 10px;
  font-weight: 750;
}

.calendar-event-detail-row--schedule {
  grid-column: 1 / -1;
  padding-bottom: 12px;
  border-bottom: 1px solid #EDF1F5;
}

.calendar-event-note {
  grid-column: 1 / -1;
  padding: 11px 12px;
  border: 1px solid #E9EEF3;
  border-radius: 10px;
  background: #F9FBFC;
}

.calendar-event-note span {
  display: block;
  margin-bottom: 5px;
  color: #7B8B9D;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.calendar-event-note span i {
  margin-right: 5px;
  color: #1E958F;
}

.calendar-event-note p {
  margin: 0;
  color: #526174;
  font-size: 11px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.calendar-page-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.calendar-page-actions .btn {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0.55rem 1rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
}

.calendar-page-actions i {
  margin: 0 !important;
}

.calendar-overview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 14px;
  padding: 16px 20px;
  border: 1px solid #DCEAE8;
  border-radius: 16px;
  background:
    radial-gradient(circle at 8% 20%, rgba(30, 149, 143, 0.1), transparent 35%),
    linear-gradient(135deg, #FFFFFF 0%, #F7FBFB 100%);
  box-shadow: 0 8px 24px rgba(20, 50, 99, 0.045);
}

.calendar-overview__intro {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.calendar-overview__icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #14608F, #1E958F);
  box-shadow: 0 7px 16px rgba(30, 149, 143, 0.2);
  color: #FFFFFF;
}

.calendar-overview__intro span,
.calendar-overview__intro strong {
  display: block;
}

.calendar-overview__intro span {
  margin-bottom: 2px;
  color: #8796A7;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.calendar-overview__intro strong {
  color: #1E293B;
  font-size: 15px;
  font-weight: 800;
  text-transform: capitalize;
}

.calendar-overview__stats {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.calendar-stat {
  min-width: 104px;
  padding: 9px 13px;
  border: 1px solid #E4EBF2;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.88);
}

.calendar-stat span,
.calendar-stat strong {
  display: block;
}

.calendar-stat span {
  color: #8A99AA;
  font-size: 10px;
  font-weight: 700;
}

.calendar-stat strong {
  margin-top: 2px;
  color: #315DB5;
  font-size: 17px;
  font-weight: 850;
}

.calendar-stat--today strong { color: #1E958F; }
.calendar-stat--doctor strong { color: #7254B3; }

.calendar-workspace {
  grid-template-columns: 272px minmax(0, 1fr);
}

.calendar-side-card,
.calendar-main {
  border-radius: 16px;
}

.calendar-status-list {
  align-items: stretch;
  gap: 8px;
  width: 100%;
}

.calendar-status-chip {
  width: 100%;
  min-height: 38px;
  justify-content: flex-start;
  padding: 8px 12px;
  border: 0;
  border-radius: 10px;
  font-size: 12px;
  text-align: left;
}

.calendar-status-chip > span {
  flex: 1 1 auto;
  text-align: left;
}

.calendar-status-chip--active {
  border: 0 !important;
  box-shadow: 0 4px 12px rgba(20, 50, 99, 0.1) !important;
  transform: translateY(-1px);
}

.appointment-calendar-page .calendar-status-chip,
.appointment-calendar-page .calendar-status-chip[class*="calendar-status-chip--"] {
  border-width: 0 !important;
  border-style: none !important;
  outline: 0 !important;
}

.calendar-overview__icon i,
.calendar-section-icon i,
.calendar-main .v-event,
.calendar-main .v-event * {
  color: #FFFFFF !important;
}

.calendar-main__body {
  position: relative;
  height: 640px;
}

.calendar-main__body--loading > .v-calendar {
  opacity: 0.55;
}

.calendar-feedback,
.calendar-empty-hint {
  position: absolute;
  z-index: 4;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
}

.calendar-feedback {
  min-width: 280px;
  flex-direction: column;
  padding: 22px;
  border: 1px solid #E4EBF2;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 14px 34px rgba(20, 50, 99, 0.12);
  text-align: center;
}

.calendar-feedback--loading {
  flex-direction: row;
  gap: 10px;
  color: #64748B;
  font-size: 12px;
  font-weight: 700;
}

.calendar-feedback--error > div {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  margin-bottom: 9px;
  border-radius: 12px;
  background: #FFF0F0;
  color: #C44545;
}

.calendar-feedback--error strong {
  color: #7F3030;
  font-size: 13px;
}

.calendar-feedback--error span {
  margin: 4px 0 12px;
  color: #947171;
  font-size: 11px;
}

.calendar-feedback--error button {
  padding: 7px 13px;
  border: 0;
  border-radius: 8px;
  background: #14608F;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 750;
}

.calendar-empty-hint {
  top: 86px;
  transform: translateX(-50%);
  gap: 7px;
  padding: 7px 12px;
  border: 1px solid #E7EDF4;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #8796A7;
  font-size: 11px;
  font-weight: 700;
  pointer-events: none;
}

.calendar-event-popover__footer {
  grid-column: 1 / -1;
  padding-top: 3px;
}

.calendar-event-popover__footer button {
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 9px;
  background: #EDF7F6;
  color: #157B76;
  font-size: 11px;
  font-weight: 800;
}

@media (max-width: 1199.98px) {
  .calendar-workspace {
    grid-template-columns: 250px minmax(0, 1fr);
    gap: 14px;
  }
}

@media (max-width: 991.98px) {
  .calendar-workspace {
    grid-template-columns: 1fr;
  }

  .calendar-sidebar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: stretch;
  }

  .calendar-status-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .calendar-sidebar {
    grid-template-columns: 1fr;
  }

  .calendar-page-actions,
  .calendar-overview {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
  }

  .calendar-page-actions .btn {
    flex: 1 1 auto;
  }

  .calendar-overview__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .calendar-stat {
    min-width: 0;
  }

  .calendar-main__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .calendar-toolbar-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .calendar-view-button {
    margin-left: auto;
  }

  .calendar-main__body {
    height: 540px;
  }

  .calendar-event-popover__content {
    grid-template-columns: 1fr;
  }

  .calendar-event-detail-row--schedule,
  .calendar-event-note,
  .calendar-event-popover__footer {
    grid-column: auto;
  }
}

@media (max-width: 479.98px) {
  .calendar-side-card {
    padding: 15px;
    border-radius: 16px;
  }

  .calendar-main {
    border-radius: 16px;
  }

  .calendar-main__toolbar {
    padding: 14px;
  }

  .appointment-calendar-page .appointment-calendar-shell > .card-header .card-label {
    font-size: 18px;
  }

  .calendar-view-button {
    margin-left: 0;
  }

  .calendar-page-actions,
  .calendar-overview__stats,
  .calendar-status-list {
    grid-template-columns: 1fr;
  }

  .calendar-page-actions .btn {
    width: 100%;
  }
}
</style>
